// [UNTAPPED-v2] Untapped Opportunities (Legacy Frontend Component)
// Uses /api/untapped-opportunities endpoints
// Tables: affiliate_clean (source) + untapped_validation_cache (cache)
// Controller: untapped.js + streamingController.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Globe,
  X,
  Flag,
  TrendingUp,
  MousePointer,
  ExternalLink,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import UniversalTable from '../../services/universalTable';
import { CountryFlag } from '../../services/countryFlagService';
import { countryList, getCountryName } from '../../services/countryList';

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Progress Bar Component
const ProgressBar = ({ progress, message }) => {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-80 bg-gray-200 dark:bg-slate-700 rounded-full h-3 shadow-inner">
        <div
          className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-lg"
          style={{ width: `${progress}%` }}
        >
          <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full opacity-50 animate-pulse"></div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        <span className="text-lg text-gray-700 dark:text-slate-100 font-medium">{message}</span>
      </div>
      <div className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
        {progress}% Complete
      </div>
    </div>
  );
};

// Streaming Validation Loader Component - shows at bottom of table while validating
const ValidationLoader = ({ currentUrl, validatedCount, totalExpected }) => {
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-700 border border-blue-200 dark:border-slate-600 rounded-lg p-4 mt-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          <div className="absolute inset-0 w-8 h-8 border-2 border-blue-200 dark:border-blue-800 rounded-full animate-pulse"></div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
              Validating affiliates...
            </span>
            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
              {validatedCount} found
            </span>
          </div>
          {currentUrl && (
            <p className="text-sm text-gray-600 dark:text-slate-400 truncate">
              Checking: <span className="font-mono text-blue-600 dark:text-blue-400">{currentUrl}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const UntappedOpportunity = () => {
  const navigate = useNavigate();

  // Get brand data from Redux store
  const selectedBrand = useSelector(state => state.untappedOpportunities.selectedBrand);
  const user = useSelector(state => state.auth.user); // Get user data for plan
  const brandData = selectedBrand || {};

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false); // Don't auto-load
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [error, setError] = useState(null);
  const [hasStartedSearch, setHasStartedSearch] = useState(false); // Track if user has started a search

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Country selection state - user can select one country at a time for faster queries
  const [selectedCountry, setSelectedCountry] = useState('');

  // Get available countries from brand data
  const availableCountries = React.useMemo(() => {
    if (brandData.countries) {
      // Handle string format: "DE,AT,PL,AU,CA,IT,AE"
      if (typeof brandData.countries === 'string' && brandData.countries.length > 0) {
        return brandData.countries.split(',').map(c => c.trim()).filter(c => c.length > 0);
      }
      // Handle array format
      if (Array.isArray(brandData.countries) && brandData.countries.length > 0) {
        return brandData.countries.flatMap(c => c.split(',')).map(c => c.trim()).filter(c => c.length > 0);
      }
    }
    if (brandData.country) {
      return [brandData.country];
    }
    return [];
  }, [brandData.countries, brandData.country]);

  // Set default selected country when available countries change
  React.useEffect(() => {
    if (availableCountries.length > 0 && !selectedCountry) {
      setSelectedCountry(availableCountries[0]);
    }
  }, [availableCountries, selectedCountry]);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentValidatingUrl, setCurrentValidatingUrl] = useState('');
  const [streamedCount, setStreamedCount] = useState(0);
  const [fromCacheCount, setFromCacheCount] = useState(0);
  const eventSourceRef = useRef(null);

  // Get user plan
  const userPlan = user?.plan || 'Free';

  // Parse serp_validated_geos to get page URL for target countries
  // New format: "true,https://example.com/page" (no classification)
  const parseSerpValidation = (serpValidatedGeos, targetCountries) => {
    if (!serpValidatedGeos || !targetCountries || targetCountries.length === 0) {
      return { classification: '', validatedPage: '' };
    }

    // Get first country's validation data
    for (const country of targetCountries) {
      const validationResult = serpValidatedGeos[country];
      if (validationResult && typeof validationResult === 'string') {
        // New format: "true,https://example.com/page" or old format: "true,AFFILIATE,url"
        const parts = validationResult.split(',');
        const isFound = parts[0] === 'true';

        // Check if second part is a classification (AFFILIATE, BRAND, OTHER, NO_SERP) or a URL
        const secondPart = parts[1] || '';
        const isClassification = ['AFFILIATE', 'BRAND', 'OTHER', 'NO_SERP'].includes(secondPart.toUpperCase());

        if (isClassification) {
          // Old format: "true,AFFILIATE,url"
          const classification = secondPart;
          const validatedPage = parts.slice(2).join(',') || '';
          return { classification, validatedPage };
        } else {
          // New format: "true,url" (no classification)
          const validatedPage = parts.slice(1).join(',') || '';
          return { classification: isFound ? 'FOUND' : '', validatedPage };
        }
      }
    }
    return { classification: '', validatedPage: '' };
  };

  // Transform function to match AffiliateRecruitment structure
  const TransformDataAPI = (data) => {
    // Get target countries from brand data
    const targetCountries = brandData.countries && Array.isArray(brandData.countries) && brandData.countries.length > 0
      ? brandData.countries
      : (brandData.country ? [brandData.country] : []);

    const transformedData = data.map((item, index) => {
      // Parse serp_validated_geos for classification and page
      const { classification, validatedPage } = parseSerpValidation(item.serp_validated_geos, targetCountries);

      return {
        paid_traffic: item.adwords_traffic,
        imageUrl: item.screenshot_url || 'blurrow.png', // No plan restrictions
        paid_traffic_difference: item.adwords_traffic_difference,
        affiliate_url: item.affiliate_url,
        affiliate_url_confidence: item.affiliate_url_confidence, // Classification Source URL
        already_approached: item.already_approached,
        email: item.email,
        id: item.id || `affiliate-${index}`,
        keywords: item.keywords,
        organic_traffic: item.organic_traffic,
        organic_traffic_difference: item.organic_traffic_difference,
        paid_keyword: item.paid_keyword,
        adwords_keywords: item.adwords_keywords,
        paid_top_page: item.paid_top_page,
        rank: item.rank,
        rank_difference: item.rank_difference,
        top_geo_organic_traffic: item.top_geo_organic_traffic,
        top_geo_organic_traffic_difference: item.top_geo_organic_traffic_difference,
        top_page: item.top_page,
        total_traffic: item.total_traffic,
        total_traffic_difference: item.total_traffic_difference,
        screenshot_url: item.screenshot_url,
        // Additional fields from untapped affiliate data
        organic_keywords: item.organic_keywords,
        organic_cost: item.organic_cost,
        adwords_cost: item.adwords_cost,
        top_geos: item.top_geos,
        top_geo_rank: item.top_geo_rank,
        vertical: item.vertical,
        existing_affiliate: item.existing_affiliate,
        // New fields for classification and validated page
        serp_classification: classification,
        serp_validated_page: validatedPage
      };
    });
    return transformedData;
  };

  // Close any existing EventSource connection
  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeEventSource();
    };
  }, [closeEventSource]);

  // Fetch data using streaming API
  const fetchAffiliatesStreaming = async (page = 0, pageSize = 10, showProgress = true, search = '') => {
    if (!brandData.brand_url) {
      setLoading(false);
      setError('No brand selected. Please select a brand from the directory.');
      return;
    }

    // Close any existing connection
    closeEventSource();

    if (showProgress) {
      setLoading(true);
      setLoadingProgress(0);
      setLoadingMessage('Connecting to server...');
    } else {
      setIsLoadingTableData(true);
    }

    setError(null);
    setAffiliates([]);
    setStreamedCount(0);
    setFromCacheCount(0);
    setCurrentValidatingUrl('');

    try {
      // Use only the selected country for faster queries
      const countryToQuery = selectedCountry || (availableCountries.length > 0 ? availableCountries[0] : '');

      const requestBody = {
        brand_url: brandData.brand_url,
        brand_name: brandData.brand_name,
        brand_id: brandData.brand_id,
        vertical: brandData.vertical,
        countries: countryToQuery ? [countryToQuery] : [],
        page: page,
        pageSize: pageSize,
        search: search || searchQuery
      };


      // Use fetch with POST for SSE (EventSource only supports GET)
      const response = await fetch(`${basePath}/api/untapped-opportunities/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Hide initial loader, show streaming state
      if (showProgress) {
        setLoading(false);
      }
      setIsStreaming(true);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processEvent = (eventData) => {
        try {
          const lines = eventData.split('\n');
          let eventType = '';
          let data = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
              data = line.substring(5).trim();
            }
          }

          if (!eventType || !data) return;

          const parsed = JSON.parse(data);

          switch (eventType) {
            case 'phase':
              if (parsed.phase === 'cached') {
                setLoadingMessage('Loading cached results...');
              } else if (parsed.phase === 'validating') {
                setLoadingMessage('Validating new affiliates...');
              }
              break;

            case 'affiliate':
              // Add new affiliate to the list
              const transformed = TransformDataAPI([parsed])[0];
              setAffiliates(prev => [...prev, transformed]);
              setStreamedCount(prev => prev + 1);
              if (parsed.source === 'cache' || parsed.source === 'validated_cache') {
                setFromCacheCount(prev => prev + 1);
              }
              break;

            case 'cacheComplete':
              setTotalItems(parsed.total || 0);
              setTotalPages(parsed.totalPages || 1);
              break;

            case 'validating':
              setCurrentValidatingUrl(parsed.affiliate_url || '');
              break;

            case 'rejected':
              setCurrentValidatingUrl('');
              break;

            case 'validationError':
              console.warn('Validation error:', parsed);
              break;

            case 'complete':
              setIsStreaming(false);
              setCurrentValidatingUrl('');
              setTotalItems(parsed.total || 0);
              setIsLoadingTableData(false);
              break;

            case 'error':
              console.error('Stream error:', parsed);
              setError(parsed.message);
              setIsStreaming(false);
              setIsLoadingTableData(false);
              break;

            default:
          }
        } catch (e) {
          console.error('Error processing event:', e, eventData);
        }
      };

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by double newlines)
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const event of events) {
          if (event.trim()) {
            processEvent(event);
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        processEvent(buffer);
      }

      setIsStreaming(false);
      setIsLoadingTableData(false);

    } catch (err) {
      console.error('Error in streaming:', err);
      setError(err.message || 'Failed to fetch affiliates');
      setLoading(false);
      setIsStreaming(false);
      setIsLoadingTableData(false);
    }
  };

  // Fallback to regular fetch for pagination (uses cached endpoint for speed)
  const fetchAffiliates = async (page = 0, pageSize = 10, showProgress = true, search = '') => {
    if (!brandData.brand_url) {
      setLoading(false);
      setError('No brand selected. Please select a brand from the directory.');
      return;
    }

    // For first page, use streaming
    if (page === 0 && showProgress) {
      return fetchAffiliatesStreaming(page, pageSize, showProgress, search);
    }

    // For subsequent pages, use regular fetch (cached endpoint for speed)
    setIsLoadingTableData(true);
    setError(null);

    try {
      // Use only the selected country for faster queries
      const countryToQuery = selectedCountry || (availableCountries.length > 0 ? availableCountries[0] : '');

      const requestBody = {
        brand_url: brandData.brand_url,
        brand_name: brandData.brand_name,
        brand_id: brandData.brand_id,
        vertical: brandData.vertical,
        countries: countryToQuery ? [countryToQuery] : [],
        page: page,
        pageSize: pageSize,
        sortBy: 'country_traffic',
        sortOrder: 'desc',
        search: search || searchQuery
      };


      // Try cached endpoint first (fast), fallback to regular endpoint
      let response = await fetch(`${basePath}/api/untapped-opportunities/cached`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      // If cached endpoint fails or returns no data, use regular endpoint
      if (!response.ok) {
        response = await fetch(`${basePath}/api/untapped-opportunities`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      let affiliatesData = [];
      if (responseData.success && Array.isArray(responseData.data)) {
        affiliatesData = responseData.data;
        setTotalItems(responseData.total || 0);
        setTotalPages(responseData.totalPages || 0);
        setCurrentPage(responseData.page || 0);
        setCurrentPageSize(responseData.pageSize || pageSize);
      }

      const transformedData = TransformDataAPI(affiliatesData);
      setAffiliates(transformedData);
      setIsLoadingTableData(false);

    } catch (err) {
      console.error('Error fetching affiliates:', err);
      setError(err.message || 'Failed to fetch affiliates');
      setIsLoadingTableData(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchAffiliates(newPage, currentPageSize, false, searchQuery);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(0); // Reset to first page
    fetchAffiliates(0, newPageSize, false, searchQuery);
  };

  // Handle search - debounced server-side search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to first page when searching
    fetchAffiliates(0, currentPageSize, false, query);
  };

  // Handler to start the search when user clicks the button
  const handleStartSearch = () => {
    if (selectedCountry) {
      setHasStartedSearch(true);
      fetchAffiliates(0, 10, true, '');
    }
  };

  // Re-fetch when selected country changes (only if search has already started)
  useEffect(() => {
    if (selectedCountry && hasStartedSearch) {
      fetchAffiliates(0, 10, true, '');
    }
  }, [selectedCountry]);

  // Prepare pagination info for UniversalTable
  const paginationInfo = {
    currentPage: currentPage,
    page: currentPage,
    totalPages: totalPages,
    totalItems: totalItems,
    total: totalItems,
    pageSize: currentPageSize,
    hasNextPage: currentPage + 1 < totalPages,
    hasPreviousPage: currentPage > 0
  };

  // Use affiliates as filteredData (no client-side filtering needed since server handles pagination)
  const filteredData = affiliates;

  // Helper function to format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  // Helper function to process geo data
  const getProcessedGeos = (geoData) => {
    if (!geoData) return [];

    // Countries to exclude from display
    const excludedCountries = ['unknown', 'xx', 'mobile'];

    const trimGeo = geoData.split(',');
    return trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();

        // Skip if country is empty or in excluded list
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) {
          return null;
        }

        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;

        // Also check if the short country name is in excluded list (e.g., "XX")
        if (excludedCountries.includes(shortCountryName.toLowerCase())) {
          return null;
        }

        return {
          country: shortCountryName,
          count: count?.trim() || '0'
        };
      })
      .filter(Boolean);
  };

  // Helper function to get unique keywords
  const getUniqueKeywords = (keywordString) => {
    if (!keywordString) return [];
    const keywords = keywordString.split(',')
      .map(keyword => keyword.trim().toLowerCase())
      .filter(keyword => keyword.length > 0);
    const uniqueKeywords = [...new Set(keywords)];
    return uniqueKeywords.map(uniqueKeyword => {
      const originalKeyword = keywordString.split(',')
        .map(k => k.trim())
        .find(k => k.toLowerCase() === uniqueKeyword);
      return originalKeyword;
    }).filter(Boolean);
  };

  // Helper function to format URL
  const formatUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Custom render function for expanded row content
  const renderExpandedRow = (rowData) => {
    return (
      <div className="space-y-6">
        {/* Classification Source */}
        {rowData.affiliate_url_confidence && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h4 className="font-semibold text-gray-800 dark:text-slate-100">Classification Source</h4>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
              <div className="flex flex-col gap-2">
                {rowData.affiliate_url_confidence
                  .split(',')
                  .map(url => url.trim())
                  .filter(url => url.length > 0)
                  .map((url, index) => (
                    <a
                      key={index}
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors flex items-center gap-2"
                    >
                      <span className="break-all">{url}</span>
                      <ExternalLink className="w-4 h-4 flex-shrink-0" />
                    </a>
                  ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                Pages found during SERP validation.
              </p>
            </div>
          </div>
        )}

        {/* Country Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h4 className="font-semibold text-gray-800 dark:text-slate-100">Country Breakdown</h4>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
            {rowData.top_geo_organic_traffic ? (
              <div className="grid grid-cols-4 gap-4">
                {getProcessedGeos(rowData.top_geo_organic_traffic).map((geo, index) => (
                  <div key={index} className="flex justify-between items-center py-3 px-4 bg-gray-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <CountryFlag countryCode={geo.country} className="w-6 h-4" />
                      <span className="font-medium text-gray-800 dark:text-slate-200">{geo.country}</span>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatNumber(geo.count)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 dark:text-slate-400 text-center py-4">No country data available</div>
            )}
          </div>
        </div>

        {/* Two Column Layout for Pages and Keywords */}
        <div className="grid grid-cols-2 gap-6">
          {/* Top Pages */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Pages</h4>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
              {rowData.top_page ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {rowData.top_page.split(',').slice(0, 10).map((page, index) => (
                    <div key={index} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">{index + 1}</span>
                        <a
                          href={formatUrl(page.trim())}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block pl-6 transition-colors"
                        >
                          {page.trim()}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-slate-400 text-center py-4">No top pages available</div>
              )}
            </div>
          </div>

          {/* Top Organic Keywords */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Organic Keywords</h4>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
              {rowData.keywords ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {getUniqueKeywords(rowData.keywords).slice(0, 10).map((keyword, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-2 py-1 rounded">{index + 1}</span>
                        <span className="text-gray-700 dark:text-slate-300 text-xs">{keyword}</span>
                      </div>
                      <span className="text-green-600 dark:text-green-400 text-xs font-medium">Organic</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-slate-400 text-center py-4">No organic keywords available</div>
              )}
            </div>
          </div>
        </div>

        {/* Paid Keywords - Full Width */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MousePointer className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Paid Keywords</h4>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
            {rowData.paid_keyword ? (
              <div className="grid grid-cols-3 gap-3">
                {getUniqueKeywords(rowData.paid_keyword).slice(0, 12).map((keyword, index) => (
                  <div key={index} className="flex justify-between items-center py-2 px-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-800">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 px-2 py-1 rounded">{index + 1}</span>
                      <span className="text-gray-700 dark:text-slate-300 text-xs">{keyword}</span>
                    </div>
                    <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">Paid</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 dark:text-slate-400 text-center py-4">No paid keywords available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show full page loader while loading data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <ProgressBar progress={loadingProgress} message={loadingMessage} />
        </div>
      </div>
    );
  }

  // Show full-page error state only if no brand selected
  if (!brandData.brand_url) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-slate-950 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">No Brand Selected</h3>
            <p className="text-gray-600 dark:text-slate-400 text-center">Please select a brand from the directory to view untapped opportunities.</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">

      {/* Header section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
                Untapped Opportunities
                {brandData.brand_name && (
                  <span className="ml-2 text-blue-600 dark:text-blue-400">
                    {' '}For {brandData.brand_name
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </span>
                )}
              </h1>

              <div className="space-y-1">
                {brandData.brand_url && (
                  <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                    Brand: {brandData.brand_url} | {brandData.vertical}
                  </p>
                )}
                <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                  Untapped affiliate opportunities for the selected brand
                </p>
              </div>
            </div>

            {/* Country Selector */}
            {availableCountries.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
                    Target Country:
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  {selectedCountry && (
                    <CountryFlag countryCode={selectedCountry} className="w-6 h-4 rounded shadow-sm" />
                  )}
                  <div className="relative">
                    <select
                      value={selectedCountry}
                      onChange={(e) => {
                        setSelectedCountry(e.target.value);
                        setCurrentPage(0); // Reset to first page
                      }}
                      disabled={isStreaming || isLoadingTableData}
                      className="appearance-none bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg py-2 pl-4 pr-10 text-gray-700 dark:text-slate-200 font-medium cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
                    >
                      {availableCountries.map((country) => (
                        <option key={country} value={country}>
                          {getCountryName(country)}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 dark:text-slate-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                {availableCountries.length > 1 && (
                  <span className="text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">
                    {availableCountries.length} countries
                  </span>
                )}
                {/* Load/Refresh Button */}
                <button
                  onClick={handleStartSearch}
                  disabled={isStreaming || isLoadingTableData || !selectedCountry}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isStreaming || isLoadingTableData ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : hasStartedSearch ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Refresh</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Find Opportunities</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                  {error}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results section using UniversalTable */}
      {affiliates.length > 0 || isLoadingTableData || isStreaming ? (
        <>
          <UniversalTable
            data={filteredData}
            paginationInfo={paginationInfo}
            onFetchData={handlePageChange} // For page changes
            onPageSizeChange={handlePageSizeChange} // For page size changes
            onSearch={handleSearch} // For server-side search
            title="Untapped Opportunities"
            searchable={true}
            defaultPageSize={currentPageSize}
            loading={isLoadingTableData && !isStreaming}
            userPlan={userPlan} // Pass user plan to UniversalTable
            expandable={true}
            renderExpandedRow={renderExpandedRow} // Custom expanded row content
          />

          {/* Streaming validation loader */}
          {isStreaming && (
            <ValidationLoader
              currentUrl={currentValidatingUrl}
              validatedCount={streamedCount}
              totalExpected={currentPageSize}
            />
          )}

          {/* Show cache stats if loaded from cache */}
          {!isStreaming && fromCacheCount > 0 && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-400">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>
                Loaded {fromCacheCount} from cache
                {streamedCount > fromCacheCount && `, ${streamedCount - fromCacheCount} newly validated`}
              </span>
            </div>
          )}
        </>
      ) : !hasStartedSearch ? (
        /* Welcome message - user hasn't started search yet */
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6">
              <Globe className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">
              Ready to Find Opportunities
            </h3>
            <p className="text-gray-600 dark:text-slate-400 text-center max-w-md mb-6">
              Select a target country from the dropdown above and click <span className="font-semibold text-blue-600 dark:text-blue-400">"Find Opportunities"</span> to discover untapped affiliate sites for {brandData.brand_name || 'your brand'}.
            </p>
            {selectedCountry && (
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-800 px-4 py-3 rounded-lg">
                <CountryFlag countryCode={selectedCountry} className="w-8 h-6 rounded shadow-sm" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Selected Country:</p>
                  <p className="font-semibold text-gray-800 dark:text-slate-100">{getCountryName(selectedCountry)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* No results found after search */
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-400 dark:text-slate-500 mb-4">
              <AlertCircle className="w-16 h-16" />
            </div>
            <p className="text-gray-600 dark:text-slate-400 text-lg">No opportunities found</p>
            <p className="text-gray-500 dark:text-slate-500 text-sm mt-2">Try selecting a different country</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UntappedOpportunity;